import axios from 'axios';
import { cloneDeep } from 'lodash';
import { splitAgentFilter } from '@/utils/agentFilter';

export const defaultPaymentsFilters = {
  insurancePolicyId: null,
  type: null, // REGULAR, COMMISSION
  statuses: [], // UNPAID, PAID, CANCELED
  dueDateFrom: null,
  dueDateTo: null,
  searchQuery: '',
  currencyCodes: [],
  insuranceCompanyId: [],
  insuranceType: [],
  agentIds: [],
  scheduleStatuses: [], // COMPLETE, INCOMPLETE, NO_SCHEDULE — of the receipt's policy
  claraStatuses: [], // FOLLOWING_UP, NEEDS_PERSON, RESOLVED, NONE — where Clara stands on the receipt
  // Set by the link out of an import: show only the receipts that import changed. Context rather than
  // a filter the user picks in the panel, like insurancePolicyId above it.
  paymentImportRunId: null,
};

const defaultState = {
  payments: {
    items: [],
    page: 0,
    totalCount: 0,
    loading: false,
    currentPaymentId: null,
  },
  paymentFilters: { ...defaultPaymentsFilters },
};

const state = {
  ...cloneDeep(defaultState),
};

const getters = {
  paymentsList: (state) => state.payments.items,
  paymentsTotalCount: (state) => state.payments.totalCount,
  paymentsLoading: (state) => state.payments.loading,
  paymentsFilters: (state) => state.paymentFilters,
  currentPaymentId: (state) => state.payments.currentPaymentId,
  paymentsPage: (state) => state.payments.page,
};

const PAYMENTS_PAGE_LIMIT = 50;

/**
 * The run filter as the API takes it. One run from an import, or several from a carrier read -- one
 * per press of Accept -- carried in the store as one comma-separated value so the banner and its
 * clear button stay one filter. Every request that sends the filters has to go through this, or it
 * sends "runA,runB" as a single id.
 */
const withRunIds = ({ paymentImportRunId, ...filters }) => {
  const ids = paymentImportRunId ? String(paymentImportRunId).split(',').filter(Boolean) : [];
  return {
    ...filters,
    // The first one in the old single field as well: a backend without paymentImportRunIds ignores
    // an unknown field, and ignoring it would open every payment in the account instead of these.
    paymentImportRunId: ids[0] || null,
    paymentImportRunIds: ids.length ? ids : undefined,
  };
};

const actions = {
  fetchPayments({ commit, state }, { append = false } = {}) {
    commit('setPaymentsLoading', true);
    if (!append) {
      commit('setPaymentsPage', 0);
    }
    const { searchQuery, ...apiFilters } = withRunIds(state.paymentFilters);

    return axios.post('/insurance/policy/payment/list', {
      ...apiFilters,
      ...splitAgentFilter(state.paymentFilters.agentIds),
      searchQuery,
      page: state.payments.page,
      limit: PAYMENTS_PAGE_LIMIT,
    })
      .then((response) => {
        if (append) {
          commit('appendPayments', response.data);
        } else {
          commit('setPayments', response.data);
        }
        commit('setPaymentsLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching payments:', error);
        commit('setPaymentsLoading', false);
      });
  },

  loadMorePayments({ commit, dispatch, state }) {
    if (state.payments.items.length < state.payments.totalCount && !state.payments.loading) {
      commit('setPaymentsPage', state.payments.page + 1);
      return dispatch('fetchPayments', { append: true });
    }
    return Promise.resolve();
  },

  // Same filters and query as the list, so the file holds exactly the receipts on screen.
  exportPayments({ state }) {
    const { searchQuery, ...apiFilters } = withRunIds(state.paymentFilters);

    return axios.post('/insurance/policy/payment/export', {
      ...apiFilters,
      ...splitAgentFilter(state.paymentFilters.agentIds),
      searchQuery,
    })
      .then((resp) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([resp.data], { type: 'text/csv' }));
        link.download = 'Payments';
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch((error) => {
        console.error('Error exporting payments:', error);
      });
  },

  updatePaymentFilters({ commit, dispatch }, filters) {
    commit('setPaymentFilters', filters);
    dispatch('fetchPayments');
  },

  clearPaymentFilters({ commit, dispatch }) {
    commit('setPaymentFilters', { ...defaultPaymentsFilters });
    dispatch('fetchPayments');
  },

  savePayment({ commit, dispatch, state }, payment) {
    return axios.post('/insurance/policy/payment/save', payment)
      .then((response) => {
        if (payment.paymentId) {
          const updatedPayment = { ...payment };
          // If we need some data from response, we can use it here
          // Usually paymentId is returned on save
          commit('updatePaymentInList', updatedPayment);
        } else {
          dispatch('fetchPayments');
        }
      });
  },

  deletePayment({ commit, dispatch }, paymentId) {
    return axios.post('/insurance/policy/payment/delete', { paymentId })
      .then(() => {
        commit('removePaymentFromList', paymentId);
      });
  },

  deletePaymentsBulk({ commit }, paymentIds) {
    return axios.post('/insurance/policy/payment/delete-bulk', { paymentIds })
      .then(() => {
        commit('removePaymentsFromList', paymentIds);
      });
  },

  fetchPaymentById(_, paymentId) {
    return axios.post('/insurance/policy/payment/list', {
      paymentId,
      page: 0,
      limit: 1,
    }).then((response) => {
      const payments = response.data?.payments || [];
      return payments.length ? payments[0] : null;
    });
  },

  // The record of which payment imports ran, and putting one back. Under payment/ rather than import/
  // because import/history belongs to the Master Upload Wizard and these are not its runs.
  fetchPaymentImportRuns(_, {page = 0, limit = 25} = {}) {
    return axios.post('/insurance/policy/payment/import-run/list', {page, limit})
      .then((response) => response.data);
  },

  fetchPaymentImportRunDetail(_, runId) {
    return axios.post('/insurance/policy/payment/import-run/detail', {runId})
      .then((response) => response.data);
  },

  // `insurancePolicyId` undoes one policy of the run; omitted, the whole run. There is no row-level
  // undo to ask for: settling one receipt rewrites the amounts of the others on that policy, so a
  // policy is the smallest thing that can be put back consistently.
  undoPaymentImportRun(_, {runId, insurancePolicyId = null}) {
    return axios.post('/insurance/policy/payment/import-run/undo', {runId, insurancePolicyId})
      .then((response) => response.data);
  },

  // Overdue receipts Clara never wrote about and no open case covers, whatever the page's own
  // filters: the Clara banner. NEVER, not the filter's NONE: NONE also holds receipts of a case
  // the team stopped, whose client did hear from Clara.
  countOverdueWithoutClara() {
    return axios.post('/insurance/policy/payment/list', {
      statuses: ['OVERDUE'],
      claraStatuses: ['NEVER'],
      page: 0,
      limit: 1,
    }).then((resp) => (resp.data && resp.data.totalCount) || 0);
  },

  fetchPaymentTriggers() {
    return axios.post('/insurance/policy/payment/trigger/list', {});
  },

  fetchPaymentTriggerDetails(_, triggerId) {
    return axios.post('/insurance/policy/payment/trigger/detail', { triggerId });
  },

  savePaymentTrigger(_, trigger) {
    return axios.post('/insurance/policy/payment/trigger/save', trigger);
  },

  deletePaymentTrigger(_, triggerId) {
    return axios.post('/insurance/policy/payment/trigger/delete', { triggerId });
  },

  fetchPaymentTriggerHistory(_, triggerId) {
    return axios.post('/insurance/policy/payment/trigger/history/list', { triggerId });
  },

  retryPaymentTriggerHistoryItem(_, { triggerId, itemId }) {
    return axios.post('/insurance/policy/payment/trigger/history/retry', { triggerId, itemId });
  },

  // Every payment trigger's history at once — the Activity log. Filters go to the server, which also
  // decides which rows this operator is allowed to see.
  fetchPaymentTriggerActivity(_, payload) {
    return axios.post('/insurance/policy/payment/trigger/history/all', payload);
  },

  retryPaymentTriggerActivityBulk(_, payload) {
    return axios.post('/insurance/policy/payment/trigger/history/retry-bulk', payload);
  },
};

const mutations = {
  updatePaymentInList(state, updatedPayment) {
    const index = state.payments.items.findIndex((p) => p.paymentId === updatedPayment.paymentId);
    if (index !== -1) {
      const payment = state.payments.items[index];
      const newPayment = { ...payment, ...updatedPayment };

      // A due or collection date edited in place invalidates the server-computed day counts.
      // Drop them rather than leave numbers that no longer describe the dates on screen; the next
      // load brings the real ones.
      //
      // daysOverdue goes with daysLate. Dropping only the first left the other one standing at its
      // pre-edit value, which was invisible while they had a column each -- an edited row shows one
      // or the other, never both -- and is not, now that a single cell falls back to daysOverdue
      // whenever daysLate is absent. Exactly the case this branch creates.
      if (updatedPayment.dueDate || updatedPayment.paidDate) {
        if (!Number.isInteger(updatedPayment.daysLate)) delete newPayment.daysLate;
        if (!Number.isInteger(updatedPayment.daysOverdue)) delete newPayment.daysOverdue;
      }

      // Update computedStatus based on status change
      if (updatedPayment.status && updatedPayment.status !== payment.status) {
        if (updatedPayment.status === 'PAID') {
          newPayment.computedStatus = 'COMPLETED';
        } else if (updatedPayment.status === 'CANCELED') {
          newPayment.computedStatus = 'CANCELED';
        } else if (updatedPayment.status === 'UNPAID') {
          // If we don't have enough info to determine if it's UPCOMING or OVERDUE,
          // we can at least clear computedStatus so it falls back to status
          delete newPayment.computedStatus;
        }
      }

      state.payments.items.splice(index, 1, newPayment);
    }
  },
  removePaymentFromList(state, paymentId) {
    state.payments.items = state.payments.items.filter((p) => p.paymentId !== paymentId);
    state.payments.totalCount -= 1;
  },
  removePaymentsFromList(state, paymentIds) {
    const idSet = new Set(paymentIds);
    const removed = state.payments.items.filter((p) => idSet.has(p.paymentId)).length;
    state.payments.items = state.payments.items.filter((p) => !idSet.has(p.paymentId));
    state.payments.totalCount -= removed;
  },
  setPayments(state, data) {
    state.payments.items = data.payments || [];
    state.payments.totalCount = data.totalCount || 0;
    state.payments.currentPaymentId = data.currentPaymentId || null;
  },
  appendPayments(state, data) {
    state.payments.items = [...state.payments.items, ...(data.payments || [])];
    state.payments.totalCount = data.totalCount || 0;
    state.payments.currentPaymentId = data.currentPaymentId || null;
  },
  setPaymentsPage(state, page) {
    state.payments.page = page;
  },
  setPaymentsLoading(state, loading) {
    state.payments.loading = loading;
  },
  setPaymentFilters(state, filters) {
    state.paymentFilters = { ...state.paymentFilters, ...filters };
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
